import './CandidatesSection.css';
import React, { useState, useEffect, useCallback } from 'react';
import { loadCandidates, resendEmail, toggleStatus, removeCandidate } from '../../logic/candidatesLogic';
import { ActIcon } from '../../components/AdminIcons';

export default function CandidatesSection({ notify, can }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});
  const [revealedCreds, setRevealedCreds] = useState({});
  const [credEmailFailed, setCredEmailFailed] = useState({});
  const [showPass, setShowPass] = useState({});
  const isBusy = key => !!busy[key];
  const withBusy = async (key, fn) => {
    setBusy(b => ({ ...b, [key]: true }));
    try { await fn(); } finally { setBusy(b => ({ ...b, [key]: false })); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const candidates = await loadCandidates();
      setCandidates(candidates);
    } catch (e) { notify(e.response?.data?.message || 'Failed to load candidates', true); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const resendCandidateEmail = async (userId, name) => {
    await withBusy(`resend-${userId}`, async () => {
      try {
        const creds = await resendEmail(userId);
        setRevealedCreds(prev => ({ ...prev, [userId]: { username: creds.username, password: creds.newPassword } }));
        setCredEmailFailed(prev => ({ ...prev, [userId]: !creds.emailSent ? (creds.emailFailureReason || 'Email not delivered') : null }));
        setShowPass(prev => ({ ...prev, [userId]: true }));
        if (creds.emailSent) {
          notify(`New credentials emailed to ${name} — also in Credentials column`);
        } else {
          notify(
            `Email failed (${creds.emailFailureReason || 'error'}). New password is in the Credentials column — share it manually.`,
            true
          );
        }
      } catch (err) { notify(err.response?.data?.message || 'Failed', true); }
    });
  };

  const toggleCandidate = async (userId, current) => {
    await withBusy(`toggle-c-${userId}`, async () => {
      try {
        await toggleStatus(userId, current);
        notify(`Candidate ${!current ? 'activated' : 'deactivated'}`);
        load();
      } catch (err) { notify(err.response?.data?.message || 'Failed', true); }
    });
  };

  const deleteCandidate = async (userId, name) => {
    if (!window.confirm(`Delete candidate "${name}"? This cannot be undone.`)) return;
    await withBusy(`delete-c-${userId}`, async () => {
      try {
        await removeCandidate(userId);
        notify(`Candidate "${name}" deleted`);
        load();
      } catch (err) { notify(err.response?.data?.message || 'Failed', true); }
    });
  };

  return (
    <div className="fade-up">
      <div className="results-actions">
        <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>{loading?'↻ …':'↻ Refresh'}</button>
        <span className="results-count">{candidates.length} candidates</span>
      </div>
      {loading ? <div className="admin-loading"><div className="spinner"/></div>
      : candidates.length===0 ? <div className="empty-state"><span>👤</span><p>No candidates yet.</p></div>
      : <div className="results-table-wrap"><table className="results-table">
          <thead><tr><th>Candidate</th><th>Username</th><th>Credentials</th><th>Department</th><th>Position</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>{candidates.map(c=>{
            const creds = revealedCreds[c.id];
            const visible = !!showPass[c.id];
            const emailFail = credEmailFailed[c.id];
            return (
            <tr key={c.id}>
              <td><div className="candidate-cell">
                <div className="candidate-avatar">{(c.profile?.full_name||c.username||'?')[0].toUpperCase()}</div>
                <div><div className="cname">{c.profile?.full_name||'—'}</div>
                     <div className="cemail">{c.email}</div>
                     {c.profile?.phone&&<div className="cdept">📞 {c.profile.phone}</div>}</div>
              </div></td>
              <td><code className="cred-username">{c.username}</code></td>
              <td>
                {creds ? (
                  <div className="cred-box">
                    {emailFail && (
                      <div className="cred-email-warn" role="alert">
                        <strong>Email not sent:</strong> {emailFail}. Password below is valid — share it manually (phone/WhatsApp).
                      </div>
                    )}
                    <div className="cred-row"><span className="cred-label">User</span><code className="cred-val">{creds.username}</code></div>
                    <div className="cred-row">
                      <span className="cred-label">Pass</span>
                      <code className="cred-val">{visible ? creds.password : '••••••••'}</code>
                      <button type="button" className="cred-eye" title={visible?'Hide':'Show'} onClick={()=>setShowPass(p=>({...p,[c.id]:!p[c.id]}))}>
                        {visible ? '🙈' : '👁'}
                      </button>
                    </div>
                    <div className="cred-hint">{emailFail ? 'Fix SMTP in Email Settings, then Resend again.' : 'Resend to generate a new password & email.'}</div>
                  </div>
                ) : (
                  <div className="cred-pending">
                    <div className="cred-pending-row"><span className="cred-label">User</span><code className="cred-val">{c.username}</code></div>
                    <p className="cred-hint-grey">Tap <strong>Resend</strong> to set a new password. It always appears here even if email times out.</p>
                  </div>
                )}
              </td>
              <td>{c.profile?.department||'—'}</td>
              <td>{c.profile?.position||'—'}</td>
              <td><span className={`badge ${c.is_active?'badge-green':'badge-red'}`}>{c.is_active?'Active':'Inactive'}</span></td>
              <td>
                <div className="action-btns">
                  {can('candidates','edit') && (
                    <button className="act-btn info" title="Resend credentials email" disabled={isBusy(`resend-${c.id}`)} onClick={()=>resendCandidateEmail(c.id, c.profile?.full_name||c.username)}>
                      {isBusy(`resend-${c.id}`) ? <span className="act-spinner"/> : <ActIcon type="send"/>}
                    </button>
                  )}
                  {can('candidates','edit') && (
                    <button className="act-btn warn" title={c.is_active?'Deactivate':'Activate'} disabled={isBusy(`toggle-c-${c.id}`)} onClick={()=>toggleCandidate(c.id, c.is_active)}>
                      {isBusy(`toggle-c-${c.id}`) ? <span className="act-spinner"/> : <ActIcon type={c.is_active?'lock':'unlock'}/>}
                    </button>
                  )}
                  {can('candidates','delete') && (
                    <button className="act-btn danger" title="Delete candidate" disabled={isBusy(`delete-c-${c.id}`)} onClick={()=>deleteCandidate(c.id, c.profile?.full_name||c.username)}>
                      {isBusy(`delete-c-${c.id}`) ? <span className="act-spinner"/> : <ActIcon type="trash"/>}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          );})}</tbody>
        </table></div>}
    </div>
  );
}
